import { useEffect, useState } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'

import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'

import PhoneLoginNote from '~/src/Routes/Login/PhoneLoginNote'
import { formatCountdown, maskPhoneNumber } from '~/src/Staff/Membership/PhoneVerification'
import Button from '~/src/UI/Shared/Button'
import CodeInput from '~/src/UI/Shared/Form/CodeInput'
import T from '~/src/UI/Shared/Typography'

const displayName = 'PhoneCodeEntry'

const RESEND_SECONDS = 60

const Root = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  textAlign: 'center',
}))

const Counter = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'center',
}))

const InlineButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  font: 'inherit',
  padding: 0,
}))

const PhoneCodeEntry = ({ error, phoneNumber, onEdit, onComplete, onResend, onUsePassword }) => {
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS)

  useEffect(() => {
    if (secondsLeft <= 0) return undefined
    const timer = setTimeout(() => setSecondsLeft(current => current - 1), 1000)
    return () => clearTimeout(timer)
  }, [secondsLeft])

  return (
    <Box data-testid="phone-code-entry" sx={{ px: { xs: 0, sm: 'calc(50% - 250px)' } }}>
      <Root>
        <T.H4>{i18n`Check Your Phone`}</T.H4>

        <T>
          {i18n`We've sent a 6-digit verification code to the number:`} {maskPhoneNumber(phoneNumber)}{' '}
          <InlineButton type="button" data-testid="edit-phone-button" onClick={onEdit}>
            ({i18n`Edit`})
          </InlineButton>
        </T>

        <CodeInput error={Boolean(error)} onComplete={onComplete} />

        {error ? (
          <T.Tiny data-testid="code-error" sx={{ color: 'error.main' }}>
            {error}
          </T.Tiny>
        ) : null}

        <Counter>
          <T.Tiny>{i18n`Didn't get the code?`}</T.Tiny>
          {secondsLeft > 0 ? (
            <T.Tiny data-testid="resend-countdown">
              {i18n`Resend Code in`} {formatCountdown(secondsLeft)}
            </T.Tiny>
          ) : (
            <InlineButton
              type="button"
              data-testid="resend-code-button"
              onClick={() => {
                setSecondsLeft(RESEND_SECONDS)
                onResend?.()
              }}
            >
              {i18n`Resend Code`}
            </InlineButton>
          )}
        </Counter>

        <PhoneLoginNote />
      </Root>

      <Box sx={{ mt: 'clamp(2rem, 20vh, 12rem)' }}>
        <Button fullWidth variant="outlined" data-testid="use-password-button" onClick={onUsePassword}>
          {i18n`Log in with Password instead`}
        </Button>
      </Box>
    </Box>
  )
}

PhoneCodeEntry.displayName = displayName
PhoneCodeEntry.propTypes = {
  error: PropTypes.string,
  phoneNumber: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  onResend: PropTypes.func,
  onUsePassword: PropTypes.func.isRequired,
}

export default PhoneCodeEntry
